import { inject, Injectable } from '@angular/core';
import { FolderDto, GalleryDto } from '@nusign/picflow-backend-types';
import { forkJoin, from, of, take } from 'rxjs';
import { CreateFolderDialogComponent } from '../account/dialogs/create-folder-dialog/create-folder-dialog.component';
import { PICFLOW_ANALYTICS } from '../picflow-analytics';
import { DialogService } from './dialog.service';
import { FolderService } from './folder.service';
import { GalleryService } from './gallery.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root',
})
export class DashboardDragDropService {
  private toasts = inject(ToastService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private galleryService = inject(GalleryService);
  private folderService = inject(FolderService);
  private dialog = inject(DialogService);

  dropFolder(folder: FolderDto, data: { target: string; targetType: string }) {
    if (data.targetType === 'folder') {
      if (folder.parent_folder === data.target) return;
      forkJoin([
        this.folderService.getFolder(folder.id).pipe(take(1)),
        this.folderService.getFolder(data.target).pipe(take(1)),
      ]).subscribe(([folder, targetFolder]) => {
        if (!folder.permissions?.can_edit) {
          this.toasts.add("You don't have permission to move this folder", 'error');
          return;
        }
        if (!targetFolder.permissions?.can_edit) {
          this.toasts.add("You don't have permission to move into this folder", 'error');
          return;
        }

        this.folderService.updateFolder(folder.id, { parent_folder: data.target }).subscribe();
        this.analytics.track('folder_moved', true, { folder: folder.id, target: data.target });
      });
    } else if (data.targetType === 'home') {
      this.folderService
        .getFolder(folder.id)
        .pipe(take(1))
        .subscribe(folder => {
          if (!folder.permissions?.can_edit) {
            this.toasts.add("You don't have permission to move this folder", 'error');
            return;
          }

          this.folderService.updateFolder(folder.id, { parent_folder: null }).subscribe();
          this.analytics.track('folder_moved', true, { folder: folder.id, target: null });
        });
    }
  }

  dropGallery(gallery: GalleryDto, data: { id: string; target: string; targetType: string; context: string }) {
    if (data.targetType === 'gallery') {
      forkJoin([
        this.galleryService.getGallery(gallery.id).pipe(take(1)),
        this.galleryService.getGallery(data.target).pipe(take(1)),
        data.context ? this.folderService.getFolder(data.context).pipe(take(1)) : of(undefined),
      ]).subscribe(([gallery, targetGallery, parentFolder]) => {
        if (!gallery.permissions?.can_edit || !targetGallery.permissions?.can_edit) {
          this.toasts.add("You don't have permission to move this gallery", 'error');
          return;
        }

        if (parentFolder && !parentFolder.permissions?.can_edit) {
          this.toasts.add("You don't have permission to create a folder here", 'error');
          return;
        }

        from(this.dialog.open(CreateFolderDialogComponent, { parent: data.context })).subscribe((folder: FolderDto) => {
          if (!folder) return;
          this.galleryService.updateGallery(gallery.id, { folder: folder.id });
          this.galleryService.updateGallery(data.target, { folder: folder.id });
        });
      });
    } else if (data.targetType === 'folder') {
      forkJoin([
        this.galleryService.getGallery(gallery.id).pipe(take(1)),
        this.folderService.getFolder(data.target).pipe(take(1)),
      ]).subscribe(([gallery, targetFolder]) => {
        if (!gallery.permissions?.can_edit) {
          this.toasts.add("You don't have permission to move this gallery", 'error');
          return;
        }
        if (!targetFolder.permissions?.can_edit) {
          this.toasts.add("You don't have permission to move into this folder", 'error');
          return;
        }

        const oldFolder = gallery.folder as string;
        this.galleryService.updateGallery(gallery.id, { folder: data.target });
        this.analytics.track('gallery_moved', true, { from: oldFolder, to: data.target });
      });
    } else if (data.targetType === 'home') {
      this.galleryService
        .getGallery(gallery.id)
        .pipe(take(1))
        .subscribe(gallery => {
          if (!gallery.permissions?.can_edit) {
            this.toasts.add("You don't have permission to move this gallery", 'error');
            return;
          }

          const oldFolder = gallery.folder as string;
          this.galleryService.updateGallery(gallery.id, { folder: null });
          this.analytics.track('gallery_moved', true, { from: oldFolder, to: null });
        });
    } else if (data.targetType === 'create-folder') {
      forkJoin([
        this.galleryService.getGallery(gallery.id).pipe(take(1)),
        data.context ? this.folderService.getFolder(data.context).pipe(take(1)) : of(undefined),
      ]).subscribe(([gallery, parentFolder]) => {
        if (!gallery.permissions?.can_edit) {
          this.toasts.add("You don't have permission to move this gallery", 'error');
          return;
        }

        if (parentFolder && !parentFolder.permissions?.can_edit) {
          this.toasts.add("You don't have permission to create a folder here", 'error');
          return;
        }

        this.folderService.createFolder('New Folder', data.context).subscribe(folder => {
          this.galleryService.updateGallery(gallery.id, { folder: folder.id });
          this.analytics.track('folder_created');
        });
      });
    }
  }
}
